import PropTypes from "prop-types";
import { Button, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import cn from "../../utilities/tailwind-merge";

const flattenColumns = (columns = []) =>
    columns.flatMap((column) => [column, ...flattenColumns(column.children)]);

/**
 * Turns the active filters into { key, label, display } chips. Labels come from `filterLabels`,
 * then the "search by" fields, then the column the filter belongs to (columns are tagged with
 * `filterKey` by useTableFilters).
 */
export const buildFilterChips = ({ filters, columns, searchFields, searchKey, filterLabels, formatFilterValue, t }) => {
    const columnsByKey = {};
    flattenColumns(columns).forEach((column) => {
        if (column.filterKey && !columnsByKey[column.filterKey]) columnsByKey[column.filterKey] = column;
    });

    return Object.entries(filters ?? {}).map(([key, value]) => {
        const column = columnsByKey[key];
        const fieldLabel = searchFields?.find((field) => field.key === key)?.label;
        const columnTitle = typeof column?.title === "string" ? column.title : undefined;
        const label = filterLabels?.[key] ?? fieldLabel ?? (key === searchKey ? t("search") : columnTitle) ?? key;
        const optionLabel = column?.filterOptions?.find((option) => option.value === value)?.label;
        const display = formatFilterValue?.(key, value) ?? optionLabel ?? String(value);
        return { key, label, display };
    });
};

export function ResetFiltersButton({ filters, className, iconOnly = false, size }) {
    const { t } = useTranslation("table");
    return (
        <Button
            size={size}
            className={className}
            icon={<ReloadOutlined />}
            disabled={!filters.hasActiveFilters}
            onClick={filters.resetFilters}
            aria-label={t("resetFilters")}>
            {!iconOnly && t("resetFilters")}
            {filters.activeCount > 0 && (
                <span className="ml-1 rounded-full bg-[#5D5DF6] px-1.5 text-[11px] font-semibold leading-[18px] text-white">
                    {filters.activeCount}
                </span>
            )}
        </Button>
    );
}

ResetFiltersButton.propTypes = {
    filters: PropTypes.shape({
        hasActiveFilters: PropTypes.bool.isRequired,
        activeCount: PropTypes.number.isRequired,
        resetFilters: PropTypes.func.isRequired,
    }).isRequired,
    className: PropTypes.string,
    iconOnly: PropTypes.bool,
    size: PropTypes.oneOf(["small", "middle", "large"]),
};

export default function FilterChips({ chips, onRemove, onClearAll, className }) {
    const { t } = useTranslation("table");
    if (chips.length === 0) return null;

    return (
        <div className={cn("flex flex-wrap items-center gap-2 text-xs", className)}>
            <span className="text-gray-500">{t("filteredBy")}</span>
            {chips.map((chip) => (
                <Tag
                    key={chip.key}
                    closable
                    onClose={(e) => {
                        e.preventDefault();
                        onRemove(chip.key);
                    }}
                    className="!m-0 !flex max-w-full items-center rounded-full !border-[#D9D9FB] !bg-[#F3F3FE] !px-2.5 !py-0.5 text-xs !text-[#3F3FBF]">
                    <span className="shrink-0 text-gray-500">{chip.label}:</span>&nbsp;
                    <span className="max-w-[16rem] truncate font-medium" title={String(chip.display)}>
                        {chip.display}
                    </span>
                </Tag>
            ))}
            <Button type="link" size="small" className="!px-1 text-xs" onClick={onClearAll}>
                {t("clearAll")}
            </Button>
        </div>
    );
}

FilterChips.propTypes = {
    chips: PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node, display: PropTypes.node }),
    ).isRequired,
    onRemove: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
    className: PropTypes.string,
};
